import React, { Fragment } from 'react';
import { default as config } from '../config';

import { forgotPassword, saveUser, removeUser } from '../actions/account';
import { useSelector } from 'react-redux';

import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import UserSection from '../components/user/UserSection.jsx';
import Input from '../components/form/FormikInput.jsx';
import Checkbox from '../components/form/FormikCheckbox.jsx';
import Select from '../components/form/FormikSelect.jsx';
import Alert from 'react-s-alert';
import { useFormik } from 'formik';
import { object, string, boolean, date, array, addMethod } from 'yup';
import { useUser } from './User';
import Loading from '../components/Loading';
import CopyToClipboard from '../components/CopyToClipboard';

export const userPermissions = {
    ADMIN: 'admin',
    READONLY: 'admin_readonly',
    CONTENT_ADMIN: 'content_admin',
    PERMISSIONS_ADMIN: 'permissions_admin'
};

/**
 * string().phone()
 * @param {String} message message to display when invalid.
 */
// eslint-disable-next-line no-template-curly-in-string
addMethod(string, 'phone', function (message = '${path} must be a valid phone number') {
    const rPHONE =
        /^(1\s*[-/.]?)?(\((\d{3})\)|(\d{3}))\s*[-/.]?\s*(\d{3})\s*[-/.]?\s*(\d{4})\s*(([xX]|[eE][xX][tT])\.?\s*(\d+))*$/;
    return this.matches(rPHONE, {
        name: 'phone',
        message,
        excludeEmptyString: false
    });
});

const UserDashboard = (props) => {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const session = useSelector((state) => state.session);
    const isSuperAdmin = session?.permissions?.includes(userPermissions.PERMISSIONS_ADMIN);
    const { user, org, inProgress, handleUserUpdate } = useUser(params.user_id);
    let isReadOnly = (session?.permissions || []).includes(userPermissions.READONLY);

    const saveAccountInfo = async (values) => {
        // clean
        values.netsuite_entity = values.netsuite_entity.replace(/\W/gm, '').trim();
        try {
            let { data } = await saveUser(user._id, values);
            Alert.success('User Info Updated');
            handleUserUpdate(data);
        } catch {
            Alert.error('Error updating user info');
        }
    };
    const { values, errors, touched, handleSubmit, handleChange, handleBlur } = useFormik({
        initialValues: {
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            netsuite_entity: user.netsuite_entity || '',
            default_bp_packaging: user.default_bp_packaging || '',
            default_binding: user.default_binding || '',
            is_approved: user.is_approved || false,
            ils_enabled: user.ils_enabled || false,
            pif_enabled: user.pif_enabled || false,
            pif_contact: user.pif_contact || false,
            pif_start_date: user.pif_start_date || '',
            permissions: user.permissions || [],
            sites: user.sites || []
        },
        validationSchema: object().shape({
            name: object().shape({
                given: string().required().label('First Name'),
                family: string().required().label('Last Name')
            }),
            email: string().email().label('Email'),
            phone: string().phone().label('Phone Number'),
            netsuite_entity: string()
                .required()
                .label('Org ID')
                .transform((value) => {
                    return value.replace(',', '').trim();
                }),
            default_binding: string().label('Default Binding'),
            default_bp_packaging: string().label('Default Bookpacks Packaging'),
            is_approved: boolean().label('Approved for web'),
            ils_enabled: boolean().label('ILS Enabled'),
            pif_enabled: boolean().label('PIF Enabled'),
            pif_contact: boolean().label('PIF Contact'),
            pif_start_date: date().label('PIF Start Date'),
            permissions: array(string()).label('Permissions'),
            sites: array().of(string()).label('Allowed Sites')
        }),
        enableReinitialize: true,
        onSubmit: saveAccountInfo
    });

    const handleForgotPassword = () => {
        forgotPassword(user.email);
    };

    const handleDeleteUser = async (e) => {
        e.preventDefault();
        if (window.confirm(`Are you sure you want to remove ${user.email}?`)) {
            try {
                const resp = await removeUser(user._id);
                if (!resp) {
                    return;
                }
                Alert.success('User Removed');
                navigate(location?.state?.from?.pathname || '/');
            } catch (error) {
                Alert.error(`There was an error removing this user. ${error.message}`);
            }
        }
    };

    let nsId = user.netsuite_entity || user.org_id;
    let canActivate = nsId && nsId !== config.browse_netsuite_entity;

    if (inProgress) return <Loading />;
    if (isReadOnly && session._id !== user._id) {
        return (
            <>
                <ul className="list-flat">
                    <li>
                        <strong>Last Login:</strong>{' '}
                        {user.last_login ? new Date(user.last_login).toLocaleDateString('en-US') : 'unknown'}
                    </li>
                    <li>
                        <strong>Organization:</strong>{' '}
                        {org && org.name ? <Link to={`/org/${nsId}`}>{org.name.long}</Link> : 'Unknown'}
                    </li>
                    <li>
                        <strong>ID:</strong> {user._id} <CopyToClipboard text={user._id} />
                    </li>
                </ul>

                <ul className="list-flat">
                    <li>
                        <strong>Name:</strong> {user.name.given} {user.name.family}
                    </li>
                    <li>
                        <strong>Email:</strong> {user.email}
                    </li>
                    <li>
                        <strong>Phone Number:</strong> {user.phone}
                    </li>
                    <li>
                        <strong>Is Approved:</strong> {user.is_approved ? 'Yes' : 'No'}
                    </li>
                    <li>
                        <strong>ILS Enabled:</strong> {user.ils_enabled ? 'Yes' : 'No'}
                    </li>
                    <li>
                        <strong>Permissions:</strong> {(user.permissions || []).join(', ')}
                    </li>
                    <li>
                        <strong>PIF Enabled:</strong> {user.pif_enabled ? 'Yes' : 'No'}
                    </li>
                    <li>
                        <strong>PIF Contact:</strong> {user.pif_contact ? 'Yes' : 'No'}
                    </li>
                    <li>
                        <strong>PIF Start Date:</strong> {user.pif_start_date || ''}
                    </li>
                </ul>
            </>
        );
    }

    return (
        <Fragment>
            {location?.state?.from && (
                <div className="mb-6">
                    <Link to={location.state.from.pathname}>&#60; Back to {location.state.from.title}</Link>
                </div>
            )}
            {!user.netsuite_entity && <div className="flash flash-error">NOT ASSIGNED TO AN ORG.</div>}

            <UserSection header="Personal Information">
                <ul className="list-flat">
                    <li>
                        <strong>Last Login:</strong>{' '}
                        {user.last_login ? new Date(user.last_login).toLocaleDateString('en-US') : 'unknown'}
                    </li>
                    <li>
                        <strong>Organization:</strong>{' '}
                        {org && org.name ? <Link to={`/org/${nsId}`}>{org.name.long}</Link> : 'Unknown'}
                    </li>
                    <li>
                        <strong>ID:</strong> {user._id} <CopyToClipboard text={user._id} />  <a target="_blank" rel="noreferrer" href={`https://shop.playaway.com/login?cid=${user._id}`}>Assume Identity</a>
                    </li>
                </ul>
                <div>
                    <button className="button full-xs auto-md spaced-xs collapse-md" onClick={handleForgotPassword}>
                        Forgot Password
                    </button>{' '}
                    <button className="button button-danger full-xs auto-md" onClick={handleDeleteUser}>
                        Remove User
                    </button>
                </div>
            </UserSection>

            <UserSection header="Account Information">
                <div>
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="row">
                            <div className="col-xs-12 col-md-6">
                                <Input
                                    id="name.given"
                                    label="First Name"
                                    type="text"
                                    value={values?.name?.given}
                                    error={errors?.name?.given}
                                    touched={touched?.name?.given}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-xs-12 col-md-6">
                                <Input
                                    id="name.family"
                                    label="Last Name"
                                    type="text"
                                    value={values?.name?.family}
                                    error={errors?.name?.family}
                                    touched={touched?.name?.family}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-xs-12 col-md-6">
                                <Input
                                    id="email"
                                    label="Email Address"
                                    type="email"
                                    value={values.email}
                                    error={errors.email}
                                    touched={touched?.email}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-xs-12 col-md-6">
                                <Input
                                    id="phone"
                                    label="Phone Number"
                                    type="tel"
                                    value={values.phone}
                                    error={errors.phone}
                                    touched={touched?.phone}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-xs-12 col-md-4">
                                <Input
                                    label="Org ID"
                                    id="netsuite_entity"
                                    value={values.netsuite_entity}
                                    error={errors.netsuite_entity}
                                    touched={touched?.netsuite_entity}
                                    type="text"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                />
                            </div>
                            <div className="col-xs-12 col-md-4">
                                <Select
                                    label="Default Bookpacks Packaging"
                                    name="default_bp_packaging"
                                    id="default_bp_packaging"
                                    value={values.default_bp_packaging}
                                    error={errors.default_bp_packaging}
                                    touched={touched?.default_bp_packaging}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    <option value="">None Selected</option>
                                    {config.pkg_types.map((pkg) => {
                                        return (
                                            <option key={`bp_option_${pkg.key}`} value={pkg.key}>
                                                {pkg.name}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </div>
                            <div className="col-xs-12 col-md-4">
                                <Select
                                    label="Default Binding"
                                    name="default_binding"
                                    id="default_binding"
                                    value={values.default_binding}
                                    error={errors.default_binding}
                                    touched={touched?.default_binding}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                >
                                    <option value="">None Selected</option>
                                    {config.binding_types.map((pkg) => {
                                        return (
                                            <option key={`bp_option_${pkg.key}`} value={pkg.key}>
                                                {pkg.name}
                                            </option>
                                        );
                                    })}
                                </Select>
                            </div>
                        </div>

                        <div className="spaced-single">
                            <ul className="list-flat">
                                <li>
                                    <Checkbox
                                        label="Approved for web"
                                        id="is_approved"
                                        checked={values.is_approved}
                                        error={errors?.is_approved}
                                        touched={touched?.is_approved}
                                        disabled={!canActivate}
                                        inline
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </li>
                                <li>
                                    <Checkbox
                                        label="ILS Enabled"
                                        id="ils_enabled"
                                        checked={values.ils_enabled}
                                        error={errors?.ils_enabled}
                                        touched={touched?.ils_enabled}
                                        disabled={!canActivate}
                                        inline
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                    />
                                </li>
                            </ul>
                        </div>

                        {isSuperAdmin && (
                            <Select
                                className="spaced-single"
                                label="Permissions"
                                id="permissions"
                                value={values.permissions}
                                error={errors.permissions}
                                touched={touched?.permissions}
                                multiple
                                onChange={handleChange}
                                onBlur={handleBlur}
                            >
                                {Object.values(userPermissions).map((pkg) => {
                                    return (
                                        <option key={`permissions_${pkg}`} value={pkg}>
                                            {pkg}
                                        </option>
                                    );
                                })}
                            </Select>
                        )}
                        {!isSuperAdmin && (
                            <p>
                                <strong>Permissions:</strong> {(user.permissions || []).join(', ')}
                            </p>
                        )}

                        <Select
                            className="spaced-single"
                            label="Allowed Sites"
                            id="sites"
                            value={values.sites}
                            error={errors?.sites}
                            touched={touched?.sites}
                            multiple
                            onChange={handleChange}
                            onBlur={handleBlur}
                        >
                            {config.sites.map((pkg) => {
                                return (
                                    <option key={`sites_${pkg.key}`} value={pkg.key}>
                                        {pkg.label}
                                    </option>
                                );
                            })}
                        </Select>

                        <fieldset className="spaced-single">
                            <legend>Play it Forward</legend>
                            <div className="spaced-single">
                                <ul className="list-flat">
                                    <li>
                                        <Checkbox
                                            label="PIF Enabled"
                                            checked={values.pif_enabled}
                                            disabled={!canActivate}
                                            id="pif_enabled"
                                            inline
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </li>
                                    <li>
                                        <Checkbox
                                            label="PIF Contact"
                                            checked={values.pif_contact}
                                            disabled={!canActivate}
                                            id="pif_contact"
                                            inline
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        />
                                    </li>
                                </ul>
                            </div>
                            <Input
                                label="PIF Start Date"
                                id="pif_start_date"
                                type="date"
                                value={
                                    values.pif_start_date
                                        ? new Date(values.pif_start_date).toISOString().split('T')[0]
                                        : ''
                                }
                                disabled={!canActivate}
                                onChange={handleChange}
                                onBlur={handleBlur}
                            />
                        </fieldset>

                        <div>
                            <button type="submit" className="button full-xs auto-md">
                                Save Account Info
                            </button>
                        </div>
                    </form>
                </div>
            </UserSection>
        </Fragment>
    );
};
export default UserDashboard;
